import { SimulatorMetricValue, SimulatorMetrics } from './SimulatorMetrics';

let background: HTMLDivElement;
let table: HTMLTableElement;
let info: HTMLParagraphElement;
let personasLookup: Map<string, HTMLTableCellElement[]>;

export function initSimulatorUI(levelId: string, personas: string[]): void {
    if (window === undefined) return;

    if (background) background.remove();

    personasLookup = new Map<string, HTMLTableCellElement[]>();
    personas.forEach((persona) => personasLookup.set(persona, []));

    background = document.createElement('div');
    background.style.position = 'absolute';
    background.style.top = '0';
    background.style.left = '0';
    background.style.width = '100%';
    background.style.height = '100%';
    background.style.backgroundColor = 'rgba(0,0,0,0.8)';
    background.style.padding = '1em';
    background.style.boxSizing = 'border-box';
    document.body.appendChild(background);

    const container = document.createElement('div');
    container.style.padding = '2em';
    container.style.backgroundColor = '#fff';
    container.style.fontFamily = 'sans-serif';
    container.style.textAlign = 'center';
    background.appendChild(container);

    const header = document.createElement('h1');
    header.innerText = levelId;
    header.style.margin = '0';
    header.style.padding = '0';
    header.style.fontSize = '1.2em';
    header.style.fontWeight = 'bold';
    container.appendChild(header);

    const hr = document.createElement('hr');
    hr.style.margin = '1em 0';
    container.appendChild(hr);

    table = document.createElement('table');
    table.style.borderCollapse = 'collapse';
    table.style.margin = '1em 0';
    table.style.fontSize = '0.8em';
    table.style.textAlign = 'left';
    table.style.tableLayout = 'fixed';

    const tableHeader = document.createElement('thead');
    addRow(tableHeader, ['', ...personas], 'th');
    table.appendChild(tableHeader);
    container.appendChild(table);

    const emptyData = Array(personas.length).fill('');

    [
        'Average +5 Boosters',
        'Median +5 Boosters',
        'Minimum +5 Boosters',
        'Maximum +5 Boosters',
        'Average Score',
        'Median Score',
        'Minimum Score',
        'Maximum Score',
        'Wins',
        'Win percentage',
    ].forEach((stat, index) => {
        const cells = addRow(table, [stat, ...emptyData], 'td', index);
        personas.forEach((persona, index) => {
            personasLookup.get(persona)!.push(cells[index + 1]);
        });
    });

    info = document.createElement('p');
    info.style.fontSize = '0.8em';
    info.style.margin = '2em 0';
    container.appendChild(info);
}

export function updateSimulatorResultsProgress(persona: string, complete: number, attempts: number): void {
    if (window === undefined) return;

    info.innerHTML = `Simulating ${attempts} attempts for <b>${persona}</b>. ${Math.floor((complete / attempts) * 100)}% completed.`;
}

export function addSimulatorResult(metric: SimulatorMetrics): void {
    if (window === undefined) return;

    const cells = personasLookup.get(metric.player)!;
    let index = 0;
    index = addResultValue(metric.result.plus5Booster, cells, index);
    index = addResultValue(metric.result.score, cells, index);
    cells[index++].innerText = metric.result.wins.toString();
    cells[index++].innerText = ((metric.result.wins / metric.result.attempts) * 100).toFixed(2) + '%';
}

function addResultValue(value: SimulatorMetricValue, cells: HTMLTableCellElement[], index: number): number {
    if (value) {
        cells[index++].innerText = value.average.toFixed(2);
        cells[index++].innerText = value.median.toFixed(0);
        cells[index++].innerText = value.min.toFixed(0);
        cells[index++].innerText = value.max.toFixed(0);
    } else {
        cells[index++].innerText = '-';
        cells[index++].innerText = '-';
        cells[index++].innerText = '-';
        cells[index++].innerText = '-';
    }
    return index;
}

export function finishSimulatorResults(duration: number): void {
    if (window === undefined) return;

    info.innerHTML = `Finished in <b>${(duration / 1000).toFixed(2)}</b> seconds.`;
    document.addEventListener('mouseup', () => {
        background.remove();
    });
}

function addRow(
    target: HTMLTableElement | HTMLTableSectionElement,
    data: string[],
    type: 'td' | 'th' = 'td',
    rowIndex = 0
): HTMLTableCellElement[] {
    const row = document.createElement('tr');
    const cells = data.map((value, index) => {
        const align = index === 0 ? 'left' : 'right';
        const backgroundColor =
            index === 0 && type === 'th'
                ? '#fff'
                : index === 0 || type === 'th'
                    ? '#f8f8f8'
                    : (rowIndex + index) % 2 === 0
                        ? '#fafafa'
                        : '#fff';
        return addCell(row, value, type, align, backgroundColor);
    });
    target.appendChild(row);
    return cells;
}

function addCell(
    row: HTMLTableRowElement,
    value: string,
    type: 'td' | 'th',
    align: 'left' | 'right',
    backgroundColor: string
): HTMLTableCellElement {
    const cell = document.createElement(type);
    cell.innerText = value;
    cell.style.padding = '0.3em 0.5em';
    cell.style.textAlign = align;
    cell.style.backgroundColor = backgroundColor;
    row.appendChild(cell);
    return cell;
}